// [src/pages/Review/ReviewWritePage.jsx]

import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

// API & Util imports
import { API_BASE_URL } from "config/apiConfig";
import { getTokenStatus } from "utils/tokenManager";

// [BranchContext 적용]
import { useBranch } from 'contexts/BranchContext';

// TopNavigator 컴포넌트 import
import TopNavigator from "components/layout/TopNavigator/TopNavigator";

// Image imports
import starIcon from 'assets/images/icon/star-icon.svg';
import starEmptyIcon from 'assets/images/icon/star-empty-icon.svg';
// leftArrowIcon 제거 (TopNavigator 내부에서 처리)

const ReviewWritePage = () => {
    const navigate = useNavigate();
    const location = useLocation();

    // [BranchContext 적용]
    const { currentBranch } = useBranch();

    // 전달받은 state에서 mnKey 가져오기
    const { mnKey } = location.state || {};

    // brKey 결정: Context > location.state > 기본값
    const contextBrKey = currentBranch?.brKey;
    const brKey = contextBrKey || location.state?.brKey || '1';

    const [rating, setRating] = useState(5);
    const [title, setTitle] = useState("");
    const [content, setContent] = useState("");

    // handleBack 제거 (TopNavigator가 자동으로 navigate(-1) 처리)

    const handleSubmit = async () => {
        if (!title || !content) {
            alert("제목과 내용을 입력해주세요.");
            return;
        }

        if (!mnKey) {
            alert("코치 정보가 없습니다. 다시 시도해주세요.");
            navigate(-1);
            return;
        }

        const { token } = getTokenStatus();

        const payload = {
            brKey: String(brKey),
            mnKey: String(mnKey),
            title: String(title),
            cont: String(content),
            rating: String(rating)
        };

        try {
            const response = await fetch(`${API_BASE_URL}/regReview`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                    'Authorization': `Bearer ${token}`
                },
                body: new URLSearchParams(payload)
            });

            const resData = await response.json();
            const code = resData.data?.code || resData.code;

            if (code === '200' || code === 200 || resData.status === 'success') {
                alert("리뷰가 등록되었습니다.");
                navigate(-1);
            } else {
                alert(`등록 실패: ${resData.message || '오류가 발생했습니다.'}`);
            }

        } catch (e) {
            console.error("Review Submit Error:", e);
            alert("리뷰 등록 중 오류가 발생했습니다.");
        }
    };

    return (
        <div style={styles.pageContainer}>
            {/* [수정] TopNavigator 적용 (MyReview.jsx 스타일 참조) */}
            <div style={styles.fixedHeaderWrapper}>
                <div style={styles.statusBar} />
                <TopNavigator title="리뷰 작성" />
            </div>

            <main style={styles.content}>
                <div style={styles.starSection}>
                    <p style={styles.label}>만족도를 평가해주세요</p>
                    <div style={styles.starRatingContainer}>
                        {[1, 2, 3, 4, 5].map((star) => (
                            <div
                                key={star}
                                onClick={() => setRating(star)}
                                style={{ padding: '5px', cursor: 'pointer' }}
                            >
                                <img
                                    src={star <= rating ? starIcon : (starEmptyIcon || starIcon)}
                                    alt={`${star}점`}
                                    style={{
                                        ...styles.starSelectIcon,
                                        opacity: (star <= rating || !starEmptyIcon) ? 1 : 0.3
                                    }}
                                />
                            </div>
                        ))}
                    </div>
                </div>

                <div style={styles.formSection}>
                    <label style={styles.inputLabel}>제목</label>
                    <input
                        style={styles.input}
                        placeholder="제목을 입력해주세요"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        autoComplete="off"
                    />

                    <label style={styles.inputLabel}>내용</label>
                    <textarea
                        style={styles.textarea}
                        placeholder="리뷰 내용을 솔직하게 작성해주세요"
                        value={content}
                        onChange={(e) => setContent(e.target.value)}
                        autoComplete="off"
                    />
                </div>
            </main>

            <div style={styles.footer}>
                <button style={styles.submitButton} onClick={handleSubmit}>
                    작성 완료
                </button>
            </div>
        </div>
    );
};

const styles = {
    pageContainer: {
        width: '100%', minHeight: '100vh', backgroundColor: '#0D042D',
        display: 'flex', flexDirection: 'column', position: 'relative',
    },
    // [추가/수정] MyReview.jsx의 헤더 스타일 적용
    fixedHeaderWrapper: {
        position: 'fixed', top: 0, left: 0, width: '100%',
        zIndex: 100, backgroundColor: '#0D042D',
        display: 'flex', flexDirection: 'column'
    },
    statusBar: {
        flexShrink: 0, backgroundColor: 'transparent'
    },
    // Header가 Fixed로 변경되었으므로 marginTop 추가
    content: {
        flex: 1,
        padding: '24px',
        marginTop: '50px', // 헤더 높이만큼 여백 확보
        display: 'flex', flexDirection: 'column', gap: '30px',
        overflowY: 'auto', WebkitOverflowScrolling: 'touch',
        paddingBottom: '100px',
    },
    starSection: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' },
    label: { color: '#FCFCFA', fontSize: '16px', fontWeight: '500', margin: 0 },
    starRatingContainer: { display: 'flex', gap: '8px' },
    starSelectIcon: { width: '40px', height: '40px' },
    formSection: { display: 'flex', flexDirection: 'column', gap: '20px' },
    inputLabel: { color: '#D0D0D0', fontSize: '14px', fontWeight: '600', marginBottom: '-10px', marginLeft: '4px' },
    input: {
        width: '100%',
        height: '52px',
        backgroundColor: '#1B1931',
        border: '1px solid #3A394A',
        borderRadius: '12px',
        padding: '0 16px',
        color: '#FCFCFA',
        fontSize: '16px',
        fontFamily: 'Pretendard',
        boxSizing: 'border-box',
        outline: 'none',
    },
    textarea: {
        width: '100%', height: '200px', backgroundColor: '#1B1931', border: '1px solid #3A394A', borderRadius: '12px',
        padding: '16px', color: '#FCFCFA', fontSize: '16px', fontFamily: 'Pretendard', resize: 'none',
        boxSizing: 'border-box', outline: 'none', lineHeight: '1.5',
    },
    footer: {
        position: 'fixed',
        bottom: 0,
        left: 0,
        width: '100%',
        padding: '20px',
        paddingBottom: 'calc(20px + env(safe-area-inset-bottom))',
        backgroundColor: '#0D042D',
        boxSizing: 'border-box',
        zIndex: 20,
    },
    submitButton: {
        width: '100%', height: '56px', borderRadius: '12px', border: 'none',
        background: 'linear-gradient(90deg, #8052FF 0%, #0231FB 100%)',
        color: '#FFFFFF', fontSize: '18px', fontWeight: '700', cursor: 'pointer',
    }
};

export default ReviewWritePage;